import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RecordPageView } from './RecordPageView';
import { useRecordPage } from './useRecordPage';

/**
 * `/accounting/operations/record` — where a member of staff records an operation
 * without needing to know what a journal entry is.
 *
 * They pick an operation, answer its own questions in its own words, see the
 * exact journal lines it will create, and post. What comes out is an ordinary
 * posted `JournalEntry` — balanced, numbered, period-gated, reversible — carrying
 * `source: OPERATION` and linked back to the template version that produced it.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, because React boundaries do not catch errors thrown by the
 * component that renders them.
 *
 * **The boundary is for the picker's read, and only that.** An unreachable
 * picker leaves the tab with no operations, which is indistinguishable from a
 * role that may record none — and those two must never converge on the same
 * pixels, so the failed read throws and the empty list gets its own honest empty
 * state. A refused *preview* is not routed here at all: it is an answer about
 * one request, and it belongs on the field that caused it.
 */
export const RecordPage: React.FC = () => {
    const page = useRecordPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('operations.record.error.title'),
            errorMessage: t('operations.record.error.message'),
            errorRetry: t('operations.record.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('operations-record-page')}
        >
            <ErrorBoundary
                data-testid="operations-record-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="operations-record-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <RecordPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type RecordPageContentProps = {
    page: ReturnType<typeof useRecordPage>;
};

const RecordPageContent: React.FC<RecordPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    return (
        <RecordPageView
            access={page.access}
            accounts={page.accounts}
            amountError={page.amountError}
            canPost={page.canPost}
            canEditTemplates={page.canEditTemplates}
            canRecord={page.canRecord}
            dateError={page.dateError}
            dateStatus={page.dateStatus}
            form={page.form}
            formError={page.formError}
            isBalanced={page.isBalanced}
            isLoading={page.isLoading}
            isPosting={page.isPosting}
            isPreviewing={page.isPreviewing}
            jumpTo={page.jumpTo}
            locale={page.locale}
            noOperationsCopy={page.noOperationsCopy}
            onChangeOperation={page.onChangeOperation}
            onDateChange={page.onDateChange}
            onDismissRecorded={page.onDismissRecorded}
            onFieldChange={page.onFieldChange}
            onJumpToEarliest={page.onJumpToEarliest}
            onPost={page.onPost}
            onSelect={page.onSelect}
            onSlotChange={page.onSlotChange}
            onToday={page.onToday}
            onYesterday={page.onYesterday}
            operations={page.operations}
            periodLabel={page.periodLabel}
            periodNotice={page.periodNotice}
            postAmountLabel={page.postAmountLabel}
            preview={page.preview}
            recorded={page.recorded}
            selected={page.selected}
            shape={page.shape}
        />
    );
};
